import { format } from "date-fns";
import type {
  FailClass,
  IntakeCase,
  IntakeCaseType,
  BvSuccessCategory,
  BvSuccessGroup,
  StediAaaError,
  StediInactiveBenefit,
  StediInsuranceDiscovery,
  StediSubscriberDiscrepancy,
} from "@/types/intake";
import {
  CASE_TYPE_LABEL,
  CASE_TYPE_TONE,
  FAIL_CLASS_LABEL,
  FAIL_CLASS_TONE,
  BV_SUCCESS_CATEGORY_LABEL,
  BV_SUCCESS_CATEGORY_TONE,
  BV_INACTIVE_SUCCESS_CATEGORIES,
  BV_TIER_2_SUCCESS_CATEGORIES,
} from "@/types/intake";
import type { EnumBadgeSemanticTone } from "@/types";

/**
 * Parse a "yyyy-MM-dd" string as a local calendar date (midnight local time).
 *
 * NOTE: do NOT use ``parseDate`` from ``@/lib/dates`` here — it routes
 * "yyyy-MM-dd" through ``new Date(string)``, which the JS spec parses as UTC
 * midnight. In negative-UTC offsets (e.g. EDT) that lands on the *previous*
 * calendar day in local time, so any subsequent ``format()`` call produces a
 * day-off display like "Wednesday, May 6" for "2026-05-07".
 */
function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return null;
  }
  return new Date(y, mo - 1, d);
}

export function formatLocalDateLabel(ymd: string): string {
  const d = parseYmdLocal(ymd);
  if (!d) return ymd;
  try {
    return format(d, "EEE MMM d");
  } catch {
    return ymd;
  }
}

export function formatLocalDateLong(ymd: string): string {
  const d = parseYmdLocal(ymd);
  if (!d) return ymd;
  try {
    return format(d, "EEEE, MMMM d, yyyy");
  } catch {
    return ymd;
  }
}

export function caseTypeLabel(caseType: IntakeCaseType | string): string {
  return CASE_TYPE_LABEL[(caseType as IntakeCaseType) ?? "pa_status"] ?? String(caseType);
}

export function caseTypeTone(caseType: IntakeCaseType | string): EnumBadgeSemanticTone {
  return CASE_TYPE_TONE[(caseType as IntakeCaseType) ?? "pa_status"] ?? "neutral";
}

export function failClassLabel(fc: FailClass | string): string {
  return FAIL_CLASS_LABEL[(fc as FailClass) ?? "other"] ?? String(fc);
}

export function failClassTone(fc: FailClass | string): EnumBadgeSemanticTone {
  return FAIL_CLASS_TONE[(fc as FailClass) ?? "other"] ?? "neutral";
}

export function bvSuccessCategoryLabel(category: string): string {
  return (
    BV_SUCCESS_CATEGORY_LABEL[(category as BvSuccessCategory)] ??
    category.replace(/_/g, " ")
  );
}

export function bvSuccessCategoryTone(category: string): EnumBadgeSemanticTone {
  return (
    BV_SUCCESS_CATEGORY_TONE[(category as BvSuccessCategory)] ?? "neutral"
  );
}

/**
 * Classify a BV ``success_category`` into its dashboard pane group. Returns
 * ``null`` for empty / non-success categories. Display-only — see
 * ``BvSuccessGroup`` in ``@/types/intake``.
 */
export function bvSuccessGroup(category: string): BvSuccessGroup | null {
  const c = (category ?? "").trim();
  if (!c || c.toLowerCase() === "none") return null;
  if (c === "coverage_info_completely_obtained") return "bv_obtained";
  if ((BV_INACTIVE_SUCCESS_CATEGORIES as readonly string[]).includes(c)) {
    return "inactive";
  }
  if ((BV_TIER_2_SUCCESS_CATEGORIES as readonly string[]).includes(c)) {
    return "tier2";
  }
  return null;
}

/**
 * Sum a ``success_category -> count`` histogram into the dashboard pane totals.
 * Categories that don't map to a known group are ignored (they still count in
 * the overall success total, which is derived from ``live_outcome``).
 */
export function groupBvSuccessCounts(
  counts: Partial<Record<string, number>> | null | undefined,
): { bv_obtained: number; inactive: number; tier2: number } {
  const out = { bv_obtained: 0, inactive: 0, tier2: 0 };
  if (!counts) return out;
  for (const [category, raw] of Object.entries(counts)) {
    const group = bvSuccessGroup(category);
    if (!group) continue;
    out[group] += raw ?? 0;
  }
  return out;
}

export function isBvSuccessCategoryVisible(
  c: Pick<IntakeCase, "fail_class" | "case_type" | "success_category">,
): boolean {
  return (
    c.fail_class === "live_outcome" &&
    c.case_type === "benefits_verification" &&
    Boolean(c.success_category) &&
    c.success_category !== "none"
  );
}

/**
 * Primary status badge for a case row.
 *
 * A successful (``live_outcome``) BV call is broken out by its
 * ``success_category`` instead of the generic "PA outcome" bucket:
 *   - ``coverage_info_completely_obtained`` -> "BV Obtained" (green / tier 1)
 *   - any other (tier 2: patient mismatch / not found / missing info / inactive)
 *     -> the category label in a yellow/warning tone.
 * Both still live in the ``live_outcome`` success total; this only changes the
 * per-call display. Everything else falls back to the fail_class badge.
 */
export function intakeCaseStatusBadge(
  c: Pick<IntakeCase, "fail_class" | "case_type" | "success_category">,
): { label: string; tone: EnumBadgeSemanticTone } {
  if (isBvSuccessCategoryVisible(c)) {
    const label =
      c.success_category === "coverage_info_completely_obtained"
        ? "BV Obtained"
        : bvSuccessCategoryLabel(c.success_category);
    return { label, tone: bvSuccessCategoryTone(c.success_category) };
  }
  return { label: failClassLabel(c.fail_class), tone: failClassTone(c.fail_class) };
}

/** Primary detail line for intake table (fail reason or PA status). */
export function intakeCaseDetailText(
  c: Pick<
    IntakeCase,
    "fail_reason" | "pa_status" | "case_type" | "fail_class" | "success_category"
  >,
): string | null {
  if (c.fail_reason) {
    return c.fail_reason.replace(/_/g, " ");
  }
  // BV success categories are surfaced by ``intakeCaseStatusBadge`` in the
  // Status column, so they are intentionally not repeated here.
  if (c.pa_status) {
    return `PA: ${c.pa_status.replace(/_/g, " ")}`;
  }
  return null;
}

export function caseDisplayName(c: Pick<IntakeCase, "member_first_name" | "member_last_name">): string {
  return [c.member_first_name, c.member_last_name].filter(Boolean).join(" ") || "—";
}

export function shortCaseId(id: string, max: number = 12): string {
  if (!id) return "—";
  return id.length > max ? `${id.slice(0, max)}…` : id;
}

/**
 * Format an ISO timestamp (e.g. ``outbound_scheduled_for``) for display in
 * the viewer's local timezone, e.g. ``May 7, 11:42 AM``. Falls back to the
 * raw string if it can't be parsed.
 */
export function formatScheduledFor(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const s = String(iso).trim();
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  try {
    return format(d, "MMM d, h:mm a");
  } catch {
    return s;
  }
}

/** Increment ymd by N days (preserves YYYY-MM-DD format). */
export function shiftYmd(ymd: string, deltaDays: number): string {
  // Parse "yyyy-MM-dd" as a calendar date and shift in UTC so the result is
  // independent of the viewer's local timezone. Using parseDate + setDate
  // here is unsafe because `new Date("yyyy-MM-dd")` parses as UTC midnight
  // but setDate/getDate operate in local time, so in negative-UTC offsets
  // a +1 day shift can return the same calendar day.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const utc = new Date(Date.UTC(y, mo - 1, d));
  utc.setUTCDate(utc.getUTCDate() + deltaDays);
  const yyyy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Tooltip text for cases where Stedi returned different member data than submitted. */
export function stediDiscrepancyTitle(
  discrepancies: StediSubscriberDiscrepancy[],
): string {
  if (!discrepancies.length) {
    return "Stedi returned different member information than submitted";
  }
  return discrepancies
    .map(
      (d) =>
        `${d.label}: submitted "${d.submitted}" · Stedi "${d.returned}"`,
    )
    .join("\n");
}

function stediErrorText(
  err: StediAaaError,
  snakeKey: keyof StediAaaError,
  camelKey: string,
): string {
  const raw = err as StediAaaError & Record<string, string | undefined>;
  return String(raw[snakeKey] ?? raw[camelKey] ?? "").trim();
}

/** Read ``possible_resolutions`` from a persisted Stedi error object. */
export function stediErrorPossibleResolutions(err: StediAaaError): string {
  return stediErrorText(err, "possible_resolutions", "possibleResolutions");
}

/** Tooltip text for Stedi AAA or top-level eligibility errors. */
export function stediEligibilityErrorsTitle(errors: StediAaaError[]): string {
  if (!errors.length) {
    return "Stedi returned eligibility errors";
  }
  return errors
    .map((err) => {
      const code = err.code ? `[${err.code}] ` : "";
      const desc = err.description || "Unknown error";
      const location = stediErrorText(err, "location", "location")
        ? `\nLocation: ${stediErrorText(err, "location", "location")}`
        : "";
      const action = stediErrorText(err, "followup_action", "followupAction")
        ? `\nAction: ${stediErrorText(err, "followup_action", "followupAction")}`
        : "";
      const resolutions = stediErrorPossibleResolutions(err)
        ? `\nPossible resolutions: ${stediErrorPossibleResolutions(err)}`
        : "";
      return `${code}${desc}${location}${action}${resolutions}`;
    })
    .join("\n\n");
}

/** Tooltip text for Stedi AAA eligibility errors. */
export function stediAaaErrorsTitle(errors: StediAaaError[]): string {
  return stediEligibilityErrorsTitle(errors);
}

/** Tooltip text for Stedi inactive benefit coverage. */
export function stediInactiveBenefitsTitle(
  benefits: StediInactiveBenefit[],
): string {
  if (!benefits.length) {
    return "Stedi returned inactive benefit coverage";
  }
  return benefits
    .map((item) => {
      const code = item.code ? `[${item.code}] ` : "";
      const name = item.name || "Inactive";
      const services = item.service_types?.length
        ? item.service_types.join(", ")
        : item.service_type_codes?.join(", ");
      return services ? `${code}${name}: ${services}` : `${code}${name}`;
    })
    .join("\n");
}

interface StediDiscoveryBadge {
  label: string;
  tooltip: string;
  className: string;
}

/**
 * Label / tooltip / colour for the insurance-discovery outcome badge, or null when
 * discovery never ran for this case.
 */
export function stediInsuranceDiscoveryBadge(
  status: string | null | undefined,
  detail: StediInsuranceDiscovery | null | undefined,
): StediDiscoveryBadge | null {
  const normalized = String(status || "").trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  const lines: string[] = [];
  const found = detail?.coverages_found;
  if (typeof found === "number") {
    lines.push(`Coverages found: ${found}`);
  }
  if (detail?.matched_party) {
    lines.push(`Matched as: ${detail.matched_party}`);
  }
  if (detail?.matched_subscriber_name) {
    lines.push(`Policyholder: ${detail.matched_subscriber_name}`);
  }
  if (detail?.discovery_payer_name) {
    const payerId = detail.discovery_payer_id
      ? ` (${detail.discovery_payer_id})`
      : "";
    lines.push(`Payer: ${detail.discovery_payer_name}${payerId}`);
  }
  const correction = detail?.corrected_member_id;
  if (correction?.changed && correction.from && correction.to) {
    lines.push(`Member ID: ${correction.from} → ${correction.to}`);
  }
  if (detail?.matched_reasons?.length) {
    lines.push(`Verified by: ${detail.matched_reasons.join(", ")}`);
  }
  const suffix = lines.length ? `\n${lines.join("\n")}` : "";

  switch (normalized) {
    case "MATCHED":
      return {
        label: "Discovery fixed",
        tooltip: `Insurance discovery found the patient's coverage and repaired the member id; eligibility then returned benefits.${suffix}`,
        className:
          "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100",
      };
    case "NO_MATCH":
      return {
        label: "Discovery: none",
        tooltip: `Insurance discovery ran but found no coverage for this patient from their demographics (name/DOB/ZIP). Often a legal-name or missing SSN/address issue.${suffix}`,
        className:
          "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      };
    case "NO_USABLE_MATCH":
      return {
        label: "Discovery: unverified",
        tooltip: `Insurance discovery returned potential coverage, but none passed strict verification (exact DOB + last name + ZIP/state), so nothing was applied.${suffix}`,
        className:
          "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-100",
      };
    case "NO_CHANGE":
      return {
        label: "Discovery: same id",
        tooltip: `Insurance discovery confirmed the same member id already on file, so there was nothing to repair.${suffix}`,
        className:
          "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      };
    case "ERROR":
      return {
        label: "Discovery error",
        tooltip: `The insurance discovery request failed to complete.${suffix}`,
        className: "bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200",
      };
    default:
      return {
        label: `Discovery: ${normalized.toLowerCase()}`,
        tooltip: `Insurance discovery status: ${normalized}.${suffix}`,
        className:
          "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      };
  }
}
